import { iDataTableColumn } from "../../components/reusable-components/data-table/DataTable";
import {
	CorporateServiceType,
	ICorporateEvent,
	ICorporateServiceOption,
	IFlatCorporateEvent,
} from "../../interfaces/corporateEvent";
import { fDate } from "../formatTime";
import { CorporateEventStatusEnum, CorporateEventVendorStatusEnum } from "../../constants/enums";

export const getFlatCorporateEvents = (
	corporateEvents: Array<ICorporateEvent> | [],
	getCmsValue: (value: string | number) => string | number,
): Array<IFlatCorporateEvent> | [] => {
	return corporateEvents?.map((item) => {
		return {
			...item,
			eventType: getCmsValue(item.serviceEventType?.value || "CORPORATE"),
			createdBy: "YouCater Corporate Requests",
			declinedBy: item?.vendor?.status === CorporateEventVendorStatusEnum.Rejected ? "Self" : "Admin",
			showIndicator: item?.notificationCount > 0,
		} as IFlatCorporateEvent;
	});
};

export const formatBudget = (budget: number) => {
	return `AED ${budget}`;
};

export const formatPaymentDays = (days: number) => {
	return `Net ${days} ${days > 1 ? "days" : "day"}`;
};

export const formatEventDays = (days: number) => {
	return `${days} ${days > 1 ? "days" : "day"}`;
};

const humanizeServiceCode = (code: string) => {
	const words = code.toLowerCase().split("_").filter(Boolean).join(" ");
	return words.charAt(0).toUpperCase() + words.slice(1);
};

export const formatServiceTypes = (
	serviceTypes?: CorporateServiceType[] | null,
	options: ICorporateServiceOption[] = [],
	fallback = "N/A",
) =>
	(serviceTypes ?? [])
		.filter(Boolean)
		.map((code) => options.find((option) => option.value === code)?.label || humanizeServiceCode(code))
		.join(", ") || fallback;

const toDate = (value?: string | null) => (value ? new Date(value) : null);

export function getCorporateActiveEventsListColumns(): iDataTableColumn<IFlatCorporateEvent>[] {
	return [
		{
			key: "name",
			label: "Event Name",
			minWidth: 230,
			sortValue: (row) => row.name,
			render: (row) => row.name ?? "",
		},
		{
			key: "startDate",
			label: "Start Date",
			minWidth: 120,
			sortValue: (row) => toDate(row.startDate),
			sortType: "date",
			searchValue: (row) => (row.startDate ? fDate(row.startDate) : ""),
			render: (row) => (row.startDate ? fDate(row.startDate) : ""),
		},
		{
			key: "budget",
			label: "Budget",
			minWidth: 120,
			align: "right",
			sortValue: (row) => Number(row.budget) || 0,
			sortType: "number",
			searchValue: (row) => (row.budget ? formatBudget(row.budget) : ""),
			render: (row) => (row.budget ? formatBudget(row.budget) : ""),
		},
		{
			key: "paymentDays",
			label: "Payment terms",
			minWidth: 130,
			render: (row) => (row.paymentDays ? formatPaymentDays(row.paymentDays) : ""),
		},
		{
			key: "externalDeadline",
			label: "Deadline to Quote",
			minWidth: 170,
			sortValue: (row) => toDate(row.externalDeadline),
			sortType: "date",
			searchValue: (row) => (row.externalDeadline ? fDate(row.externalDeadline) : ""),
			render: (row) => (row.externalDeadline ? fDate(row.externalDeadline) : ""),
		},
		{
			key: "numberOfDays",
			label: "Event Days",
			minWidth: 110,
			align: "right",
			render: (row) => (row.numberOfDays ? formatEventDays(row.numberOfDays) : ""),
		},
		{
			key: "eventType",
			label: "Event type",
			minWidth: 140,
			searchValue: (row) => row.eventType,
			render: (row) => row.eventType,
		},
		{
			key: "createdBy",
			label: "Created By",
			minWidth: 200,
			render: (row) => row.createdBy,
		},
	];
}

export const getCorporateApprovedEventsListColumns = (): iDataTableColumn<IFlatCorporateEvent>[] => [
	...getCorporateActiveEventsListColumns(),
	{ key: "poNumber", label: "PO number", minWidth: 130, searchValue: (row) => row.poNumber, render: (row) => row.poNumber },
];

export const getCorporateDeclinedEventsListColumns = (): iDataTableColumn<IFlatCorporateEvent>[] => [
	...getCorporateActiveEventsListColumns(),
	{ key: "declinedBy", label: "Declined By", minWidth: 120, sortValue: (row) => row.declinedBy, render: (row) => row.declinedBy },
];

export const isEventDeclined = (corporateEvent: ICorporateEvent) => {
	return (
		corporateEvent?.status === CorporateEventStatusEnum.Cancelled ||
		corporateEvent?.vendor?.status === CorporateEventVendorStatusEnum.Rejected ||
		corporateEvent?.vendor?.status === CorporateEventVendorStatusEnum.Cancelled
	);
};

export const getDeclineReason = (corporateEvent: ICorporateEvent) => {
	return (
		corporateEvent?.otherCancellationReason ||
		corporateEvent?.cancellationReason?.value ||
		corporateEvent?.vendor?.otherDeclineReason ||
		corporateEvent?.vendor?.declineReason?.value
	);
};
